import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    MdClose,
    MdOpenInFull,
    MdCloseFullscreen,
    MdExitToApp,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdOutlineBookmark,
    MdOutlineBookmarkBorder,
    MdOutlineCalculate,
    MdOutlineKeyboard,
    MdOutlineLocationOn,
    MdSuperscript
} from 'react-icons/md';
import moment from 'moment';
import Button from 'components/Button';
import Loader from 'components/Loader';
import TextInput from 'components/TextInput';
import axios from 'config/axios';
import { useAppDispatch } from 'hooks/useStore';
import { setError } from 'store/error';
import { MULTIPLE_CHOICES } from 'global/constants';
import { getDeviceOS } from 'global/os';
import { generatePassageHTML } from 'global/passageHighlight';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import Reference from 'pages/private/Digital/Module/Reference';
import Shortcuts from 'pages/private/Digital/Module/Shortcuts';
import '../DigitalReview/index.css';
import './index.css';

import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";
import { flattenKatexUnderlines } from 'global/katexWrap';

const COLORS = {
    'Yellow': 'yellow',
    'Pink': 'pink',
    'Blue': 'blue'
};

const UNDERLINE = {
    'Solid': 'solid',
    'Dashed': 'dashed',
    'Dotted': 'dotted'
};

function TestFinderReview() {
    const [studentName, setStudentName] = useState('');
    const [testModules, setTestModules] = useState(null);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [moduleSelectOpen, setModuleSelectOpen] = useState(false);
    const [directionsOpen, setDirectionsOpen] = useState(false);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [referenceOpen, setReferenceOpen] = useState(false);
    const [calculator, setCalculator] = useState(null);
    const [calculatorOpen, setCalculatorOpen] = useState(false);
    const [calculatorFull, setCalculatorFull] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [showAnswer, setShowAnswer] = useState(false);
    const [shortcutKeyPressed, setShortcutKeyPressed] = useState(false);
    const [annotations, setAnnotations] = useState([]);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { testId } = useParams();
    const moduleSelectRef = useOutsideClickHandler(() => setTimeout(() => setModuleSelectOpen(false), 10));
    const directionsRef = useOutsideClickHandler(() => setTimeout(() => setDirectionsOpen(false), 10));
    const navigatorRef = useOutsideClickHandler(() => setTimeout(() => setNavigatorOpen(false), 10));

    const prevQuestion = () => {
        if (questionIndex > 0) {
            setQuestionIndex(questionIndex - 1);
        }
        else if (moduleIndex > 0) {
            setQuestionIndex(testModules[moduleIndex - 1].questions.length - 1);
            setModuleIndex(moduleIndex - 1);
        }
    }

    const nextQuestion = () => {
        if (questionIndex < testModules[moduleIndex].questions.length - 1) {
            setQuestionIndex(questionIndex + 1);
        }
        else if (moduleIndex < testModules.length - 1) {
            setModuleIndex(moduleIndex + 1);
            setQuestionIndex(0);
        }
        else {
            navigate(-1);
        }
    }

    const goToQuestion = (index: number) => {
        setQuestionIndex(index);

        let passages = document.getElementById('digitalReview__passages');
        let question = document.getElementById('digitalReview__questionChoices');

        if (passages) passages.scrollTop = 0;
        if (question) question.scrollTop = 0;
    }

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');
        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();
        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const generateAnnotation = () => {
        if (!testModules || !testModules[moduleIndex]?.answers?.[questionIndex]?.annotations) return;
        
        const newAnnotations = [...testModules[moduleIndex].answers[questionIndex].annotations];

        for (let i = 0; i < newAnnotations.length; i++) {
            const { passageId, startIndex, endIndex } = newAnnotations[i];
            const passage = document.getElementById(passageId);
            if (!passage) continue;

            const { passageHTML, passagePreview } = generatePassageHTML(passage, startIndex, endIndex);

            newAnnotations[i].html = passageHTML;
            newAnnotations[i].preview = passagePreview;
        }

        setAnnotations(newAnnotations);
    }

    const handleAnnotationMouseOver = (event: React.MouseEvent<HTMLDivElement>, annotationId: string) => {
        let bubble = document.getElementById(annotationId);
        if (bubble) {
            bubble.style.left = event.clientX + 'px';
            bubble.style.top = event.clientY + 'px';
            bubble.style.display = 'block';
        }
    }

    const handleAnnotationMouseOut = (_event: React.MouseEvent<HTMLDivElement>, annotationId: string) => {
        let bubble = document.getElementById(annotationId);
        if (bubble) {
            bubble.style.display = 'none';
        }
    }

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });
            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        const wrappingTexts = document.querySelectorAll<HTMLElement>('.katex-html .base');
        for (let i = 0; i < wrappingTexts.length; i++) {
            if (testModules[moduleIndex].subject === 'Reading and Writing') {
                wrappingTexts[i].classList.add('wrap');
            } else {
                wrappingTexts[i].classList.remove('wrap');
            }
        }

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    // Highlight ACT R/W underlined text
    const highlightACTUnderlines = () => {
        if (!testModules) return;
        
        const questions = testModules[moduleIndex]?.questions;
        if (!questions?.[questionIndex]?.passages) return;

        questions[questionIndex].passages.forEach((passageData: any) => {
            const passageElement = document.getElementById(passageData.id);
            if (!passageElement) return;

            // Remove previous highlights
            const allUnderlines = passageElement.querySelectorAll('.underline');
            allUnderlines.forEach(el => el.classList.remove('testFinderModule__activeUnderline'));

            // If this question has indices (ACT R/W), highlight the corresponding underline
            if (passageData.indices && passageData.indices.length > 0 && allUnderlines.length > 0) {
                let questionOrderInPassage = 0;
                for (let i = 0; i < questionIndex; i++) {
                    if (questions[i]?.passages?.some((p: any) => p.id === passageData.id)) {
                        questionOrderInPassage++;
                    }
                }

                if (questionOrderInPassage < allUnderlines.length) {
                    const targetUnderline = allUnderlines[questionOrderInPassage];
                    targetUnderline.classList.add('testFinderModule__activeUnderline');
                    (targetUnderline as HTMLElement).scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }
        });
    }

    const handleShortcutKeys = (event: KeyboardEvent) => {
        const { ctrlKey, altKey, code } = event;
        if (ctrlKey && altKey) {
            switch(code) {
                case 'KeyB': prevQuestion(); break;
                case 'KeyX': nextQuestion(); break;
                case 'KeyR':
                    if (testModules[moduleIndex].subject === 'Math') {
                        setReferenceOpen(!referenceOpen);
                    }
                    break;
                case 'KeyC':
                    if (testModules[moduleIndex].subject === 'Math') {
                        setCalculatorOpen(!calculatorOpen);
                    }
                    break;
            }
        }
    }

    // Fetch test data
    useEffect(() => {
        axios.get(`testfinder/review/${testId}/`)
            .then(res => {
                setStudentName(res.data.studentName);
                setTestModules(res.data.testModules);
            })
            .catch(error => {
                dispatch(setError(error));
            });
    }, [testId]);

    // Initialize calculator
    useEffect(() => {
        if (testModules) {
            let calculatorEl = document.getElementById('calculator');
            if (calculatorEl && (window as any).Desmos) {
                setCalculator((window as any).Desmos.GraphingCalculator(calculatorEl, {
                    notes: false,
                    folders: false,
                    images: false,
                    links: false
                }));
            }
            setShortcutKeyPressed(true);
        }
    }, [testModules]);

    // Render KaTeX and annotations on question change
    useLayoutEffect(() => {
        if (testModules) {
            renderKatex();
            generateAnnotation();
            highlightACTUnderlines();
        }
    }, [testModules, moduleIndex, questionIndex]);

    // Keyboard shortcuts
    useEffect(() => {
        document.addEventListener('keydown', handleShortcutKeys);
        return () => document.removeEventListener('keydown', handleShortcutKeys);
    }, [shortcutKeyPressed, moduleIndex, questionIndex, testModules]);

    if (testModules === null) return <Loader />;

    const { name, questions, answers, subject, prompt } = testModules[moduleIndex];

    return (
        <div className='digitalReviewBox' onCopy={(event) => event.preventDefault()}>
            <div className='digitalReview'>
                <div className='digitalReview__header'>
                    <div className='digitalReview__titleGroup'>
                        <button className='digitalReview__moduleSelect' onClick={() => setModuleSelectOpen(!moduleSelectOpen)}>
                            <h1 className='digitalReview__title'>{name}</h1>
                            {moduleSelectOpen
                                ? <MdKeyboardArrowUp className='digitalReview__moduleSelectIcon' />
                                : <MdKeyboardArrowDown className='digitalReview__moduleSelectIcon' />
                            }
                            {moduleSelectOpen &&
                                <div className='digitalReview__moduleSelectDropdown' ref={moduleSelectRef}>
                                    {testModules.map((testModule: any, t: number) => (
                                        <p
                                            key={t}
                                            className={t === moduleIndex
                                                ? 'digitalReview__moduleSelectDropdownItem--selected'
                                                : 'digitalReview__moduleSelectDropdownItem'
                                            }
                                            onClick={() => {
                                                if (t !== moduleIndex) {
                                                    setModuleIndex(t);
                                                    setQuestionIndex(0);
                                                }
                                            }}
                                        >
                                            {testModule.name}
                                        </p>
                                    ))}
                                </div>
                            }
                        </button>
                        <button className='digitalReview__directionsButton' onClick={() => setDirectionsOpen(!directionsOpen)}>
                            {directionsOpen ? <u>Directions</u> : <p>Directions</p>}
                            {directionsOpen
                                ? <MdKeyboardArrowUp className='digitalReview__directionsIcon' />
                                : <MdKeyboardArrowDown className='digitalReview__directionsIcon' />
                            }
                        </button>
                    </div>
                    <div className='digitalReview__timerGroup'>
                        <p className='digitalReview__timer'>
                            {formatTime(answers[questionIndex]?.timeSpent || 0)}
                        </p>
                        <p className='digitalReview__timerText'>Time spent</p>
                    </div>
                    <div className='digitalReview__actionGroup'>
                        {subject === 'Math' && <>
                            <button
                                className={referenceOpen ? 'digitalReview__action--active' : 'digitalReview__action'}
                                onClick={() => {
                                    setCalculatorOpen(false);
                                    setShortcutsOpen(false);
                                    setReferenceOpen(!referenceOpen);
                                }}
                            >
                                <MdSuperscript className='digitalReview__actionIcon' />
                                <p className='digitalReview__actionText'>Reference</p>
                            </button>
                            <button
                                className={calculatorOpen ? 'digitalReview__action--active' : 'digitalReview__action'}
                                onClick={() => {
                                    setReferenceOpen(false);
                                    setShortcutsOpen(false);
                                    setCalculatorOpen(!calculatorOpen);
                                }}
                            >
                                <MdOutlineCalculate className='digitalReview__actionIcon' />
                                <p className='digitalReview__actionText'>Calculator</p>
                            </button>
                        </>}
                        <button
                            className={shortcutsOpen ? 'digitalReview__action--active' : 'digitalReview__action'}
                            onClick={() => {
                                setCalculatorOpen(false);
                                setReferenceOpen(false);
                                setShortcutsOpen(!shortcutsOpen);
                            }}
                        >
                            <MdOutlineKeyboard className='digitalReview__actionIcon' />
                            <p className='digitalReview__actionText'>Shortcuts</p>
                        </button>
                        <button className='digitalReview__action' onClick={() => navigate(-1)}>
                            <MdExitToApp className='digitalReview__actionIcon' />
                            <p className='digitalReview__actionText'>Exit</p>
                        </button>
                    </div>
                    {directionsOpen &&
                        <div className='digitalReview__directions' ref={directionsRef}>
                            <div className='digitalReview__directionsBubble'>
                                <p className='katex digitalReview__directionsText'>{prompt || ''}</p>
                                <Button className='digitalReview__directionsClose' onClick={() => setDirectionsOpen(false)} secondary>
                                    CLOSE
                                </Button>
                            </div>
                        </div>
                    }
                    <div id='calculatorDrag' className={calculatorOpen
                        ? (calculatorFull ? 'digitalReview__popup--calculatorFull' : 'digitalReview__popup--calculator')
                        : 'digitalReview__popup--hidden'
                    }>
                        <div id='calculatorHeader' className='digitalReview__popupHeader'>
                            <h3 className='digitalReview__popupHeaderText'>Calculator</h3>
                            {calculatorFull
                                ? <MdCloseFullscreen className='digitalReview__popupHeaderFull' onClick={() => setCalculatorFull(false)} />
                                : <MdOpenInFull className='digitalReview__popupHeaderFull' onClick={() => setCalculatorFull(true)} />
                            }
                            <MdClose className='digitalReview__popupHeaderIcon' onClick={() => setCalculatorOpen(false)} />
                        </div>
                        <div id='calculator' style={{ width: calculatorFull ? 800 : 400, height: 600 }}></div>
                    </div>
                    <div id='referenceDrag' className={referenceOpen ? 'digitalReview__popup--reference' : 'digitalReview__popup--hidden'}>
                        <div id='referenceHeader' className='digitalReview__popupHeader'>
                            <h3 className='digitalReview__popupHeaderText'>Reference</h3>
                            <MdClose className='digitalReview__popupHeaderIcon' onClick={() => setReferenceOpen(false)} />
                        </div>
                        <Reference />
                    </div>
                    <div id='shortcutsDrag' className={shortcutsOpen ? 'digitalReview__popup--shortcuts' : 'digitalReview__popup--hidden'}>
                        <div id='shortcutsHeader' className='digitalReview__popupHeader'>
                            <h3 className='digitalReview__popupHeaderText'>
                                Keyboard shortcuts{getDeviceOS() === 'windows' ? ': Windows' : getDeviceOS() === 'mac_os' ? ': MacOS' : ''}
                            </h3>
                            <MdClose className='digitalReview__popupHeaderIcon' onClick={() => setShortcutsOpen(false)} />
                        </div>
                        <Shortcuts />
                    </div>
                </div>
                <div className='digitalReview__dottedLine'></div>
                <div className='digitalReview__test'>
                    {questions[questionIndex].passages.length > 0 && <>
                        <div className='digitalReview__material'>
                            <div className='digitalReview__materialContent'>
                                <div id='digitalReview__passages' className='digitalReview__passages'>
                                    {questions[questionIndex].figures.map((figure: any) => (
                                        <img
                                            className='digitalReview__figure'
                                            key={figure.id}
                                            src={figure.image}
                                            alt={(questionIndex + 1).toString()}
                                            draggable={false}
                                        />
                                    ))}
                                    {questions[questionIndex].passages.map(({ id, passage }: any) => (
                                        <div key={id} className='digitalReview__passage'>
                                            <p id={id} className='katex digitalReview__passageText'>{passage}</p>
                                            {annotations.map((annotation: any, a: number) => (
                                                <React.Fragment key={a}>
                                                    <div
                                                        className={`katex digitalReview__passageAnnotation
                                                            ${annotation.color && 'digitalReview__passageAnnotation--' + COLORS[annotation.color]}
                                                            ${annotation.underline && 'digitalReview__passageAnnotation--' + UNDERLINE[annotation.underline]}
                                                        `}
                                                        onMouseOver={(e) => handleAnnotationMouseOver(e, `digitalReview__passageAnnotationBubble${a}`)}
                                                        onMouseOut={(e) => handleAnnotationMouseOut(e, `digitalReview__passageAnnotationBubble${a}`)}
                                                        dangerouslySetInnerHTML={{ __html: annotation.html }}
                                                    ></div>
                                                    {annotation.annotation &&
                                                        <p id={`digitalReview__passageAnnotationBubble${a}`} className='digitalReview__passageAnnotationBubble'>
                                                            {annotation.annotation}
                                                        </p>
                                                    }
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className='digitalReview__verticalLine'></div>
                    </>}
                    <div className='digitalReview__question'>
                        <div className='digitalReview__questionContent'>
                            <div className='digitalReview__questionHeader'>
                                <p className={answers[questionIndex]?.isCorrect
                                    ? 'digitalReview__questionNumber--correct'
                                    : 'digitalReview__questionNumber--wrong'
                                }>
                                    {questionIndex + 1}
                                </p>
                                <button className='digitalReview__questionMarkButton'>
                                    {answers[questionIndex]?.marked
                                        ? <MdOutlineBookmark className='digitalReview__questionMarkIcon--marked' />
                                        : <MdOutlineBookmarkBorder className='digitalReview__questionMarkIcon' />
                                    }
                                    Mark for review
                                </button>
                                <button
                                    className={showAnswer ? 'digitalReview__showAnswer--active' : 'digitalReview__showAnswer'}
                                    onClick={() => setShowAnswer(!showAnswer)}
                                >
                                    {showAnswer ? 'Hide answer' : 'Show answer'}
                                </button>
                            </div>
                            <div id='digitalReview__questionChoices' className='digitalReview__questionChoices'>
                                {subject === 'Math' && questions[questionIndex].figures.map((figure: any) => (
                                    <img
                                        className='digitalReview__figure'
                                        key={figure.id}
                                        src={figure.image}
                                        alt={(questionIndex + 1).toString()}
                                        draggable={false}
                                    />
                                ))}
                                <p className='digitalReview__questionQuestion'>
                                    <span className='katex'>{questions[questionIndex].question}</span>
                                </p>
                                {questions[questionIndex].choices.length > 0 ? (
                                    <>
                                        {questions[questionIndex].choices.map((choice: any, i: number) => {
                                            const isStudentAnswer = answers[questionIndex]?.answer == choice.id;
                                            const isCorrectChoice = choice.isCorrect;
                                            
                                            let className = 'digitalReview__questionChoice';
                                            
                                            if (isStudentAnswer) {
                                                // Student selected this - show blue if correct, red if wrong
                                                className = isCorrectChoice
                                                    ? 'digitalReview__questionChoice--selected'
                                                    : 'digitalReview__questionChoice--wrong';
                                            } else if (showAnswer && isCorrectChoice) {
                                                // Not student's answer, but show correct in green when "Show answer"
                                                className = 'digitalReview__questionChoice--correct';
                                            }
                                            
                                            return (
                                            <div key={choice.id} className='digitalReview__questionChoiceWrapper'>
                                                <button className={className}>
                                                    <div className='digitalReview__questionChoiceOutline'></div>
                                                    <p className='digitalReview__questionChoiceLetter'>{MULTIPLE_CHOICES[i]}</p>
                                                    {choice.figure
                                                        ? <img className='digitalReview__questionChoiceFigure' src={choice.figure} alt={MULTIPLE_CHOICES[i]} draggable={false} />
                                                        : <p className='digitalReview__questionChoiceText'>
                                                            <span className='katex'>{choice.choice}</span>
                                                        </p>
                                                    }
                                                </button>
                                            </div>
                                            );
                                        })}
                                    </>
                                ) : (
                                    <div className='digitalReview__questionAnswerReview'>
                                        <TextInput
                                            className='digitalReview__questionAnswer'
                                            inputClassName='digitalReview__questionAnswerInput'
                                            name={name + questionIndex}
                                            value={answers[questionIndex]?.answer || ''}
                                            onChange={() => {}}
                                        />
                                        {showAnswer &&
                                            <p className='digitalReview__questionCorrectAnswer'>
                                                {questions[questionIndex].correctAnswers?.toString().replaceAll(',', ', ')}
                                            </p>
                                        }
                                    </div>
                                )}
                                <div className='digitalReview__marginBottom'></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='digitalReview__dottedLine'></div>
                <div className='digitalReview__footer'>
                    <p className='digitalReview__tester'>{studentName}</p>
                    <button
                        className='digitalReview__navigatorToggle'
                        onClick={() => setNavigatorOpen(!navigatorOpen)}
                    >
                        Question {questionIndex + 1}/{questions.length}
                        {navigatorOpen
                            ? <MdKeyboardArrowDown className='digitalReview__navigatorToggleIcon' />
                            : <MdKeyboardArrowUp className='digitalReview__navigatorToggleIcon' />
                        }
                    </button>
                    <div className='digitalReview__buttons'>
                        <Button
                            className='digitalReview__button'
                            secondary
                            disabled={moduleIndex === 0 && questionIndex === 0}
                            onClick={prevQuestion}
                        >
                            BACK
                        </Button>
                        <Button
                            className='digitalReview__button'
                            onClick={nextQuestion}
                        >
                            NEXT
                        </Button>
                    </div>
                    {navigatorOpen &&
                        <div className='digitalReview__navigator' ref={navigatorRef}>
                            <div className='digitalReview__navigatorBubble'>
                                <h2 className='digitalReview__navigatorTitle'>
                                    {name} Questions
                                </h2>
                                <div className='digitalReview__navigatorLegends'>
                                    <div className='digitalReview__navigatorLegend'>
                                        <MdOutlineLocationOn className='digitalReview__navigatorLegendCurrent' />
                                        <p className='digitalReview__navigatorLegendText'>Current</p>
                                    </div>
                                    <div className='digitalReview__navigatorLegend'>
                                        <div className='digitalReview__navigatorLegendCorrect'></div>
                                        <p className='digitalReview__navigatorLegendText'>Correct</p>
                                    </div>
                                    <div className='digitalReview__navigatorLegend'>
                                        <div className='digitalReview__navigatorLegendWrong'></div>
                                        <p className='digitalReview__navigatorLegendText'>Incorrect</p>
                                    </div>
                                    <div className='digitalReview__navigatorLegend'>
                                        <MdOutlineBookmark className='digitalReview__navigatorLegendMarked' />
                                        <p className='digitalReview__navigatorLegendText'>For review</p>
                                    </div>
                                </div>
                                <div className='digitalReview__navigatorItems'>
                                    {questions.map((question: any, i: number) => (
                                        <button
                                            key={`${question.id}nav`}
                                            className={answers[i]?.isCorrect
                                                ? 'digitalReview__navigatorItem--correct'
                                                : 'digitalReview__navigatorItem--wrong'
                                            }
                                            onClick={() => {
                                                goToQuestion(i);
                                                setNavigatorOpen(false);
                                            }}
                                        >
                                            {questionIndex === i
                                                ? <u className='digitalReview__navigatorItemNumber'>{i + 1}</u>
                                                : <p className='digitalReview__navigatorItemNumber'>{i + 1}</p>
                                            }
                                            {questionIndex === i && <MdOutlineLocationOn className='digitalReview__navigatorItemCurrent' />}
                                            {answers[i]?.marked && <MdOutlineBookmark className='digitalReview__navigatorItemMarked' />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}

export default TestFinderReview;
